import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { GlobalService } from 'src/app/api/global.service';
import {
  CalculateRelativeTaskDates, SetUseProjectDatesOffsets, TaskDateError, TaskDueDateLabel, TaskStartDateLabel, UsesProjectDates
} from 'src/app/services/task-project-dates.util';

type TaskDateMode = 'project' | 'relative' | 'fixed';

// Edits just a task's dates - the same options as the Task Dates card in task create, for editing
// straight from a task list. Works on a copy and only hands the date fields back on Save, so Cancel
// leaves the task untouched.
@Component({
  selector: 'app-task-dates',
  templateUrl: './task-dates.component.html',
})
export class TaskDatesComponent implements OnInit {

  @Input() task: any;
  @Input() project: any;

  draft: any = {};
  mode: TaskDateMode = 'project';

  constructor(private modalController: ModalController, public globalService: GlobalService) { }

  ngOnInit() {
    const t = this.task || {};
    this.draft = {
      use_project_dates: UsesProjectDates(t),
      relative_date: !!t.relative_date,
      relative_to_start: t.relative_to_start !== false,
      start_date_relative: t.start_date_relative ?? 0,
      start_date_relative_unit: t.start_date_relative_unit || 'days',
      due_date_relative: t.due_date_relative ?? 3,
      due_date_relative_unit: t.due_date_relative_unit || 'days',
      start_on_project_start: !!t.start_on_project_start,
      due_on_project_end: !!t.due_on_project_end,
      start_date: t.start_date ? String(t.start_date).substring(0, 10) : '',
      due_date: t.due_date ? String(t.due_date).substring(0, 10) : '',
    };
    this.mode = this.draft.use_project_dates ? 'project' : this.draft.relative_date ? 'relative' : 'fixed';
    // A repeating project can't have fixed task dates - they'd be wrong for every project after the first
    if (this.mode === 'fixed' && this.project?.repeat) this.SetMode('relative');
    else this.Recalculate();
  }

  SetMode(mode: TaskDateMode) {
    if (mode === 'fixed' && this.project?.repeat) return;
    const wasProject = this.mode === 'project';
    this.mode = mode;
    this.draft.use_project_dates = mode === 'project';
    this.draft.relative_date = mode !== 'fixed';
    if (mode === 'project') {
      SetUseProjectDatesOffsets(this.draft);
    }
    else if (mode === 'relative' && (wasProject || UsesProjectDates({ ...this.draft, use_project_dates: false }))) {
      // Leaving "use project dates" - the offsets that stand for it would still read as project dates
      this.draft.relative_to_start = true;
      this.draft.start_date_relative = 0;
      this.draft.start_date_relative_unit = 'days';
      this.draft.due_date_relative = 3;
      this.draft.due_date_relative_unit = 'days';
      this.draft.start_on_project_start = false;
      this.draft.due_on_project_end = false;
    }
    // Fixed keeps whatever dates the previous option worked out, as a starting point
    this.Recalculate();
  }

  Recalculate() {
    if (this.mode !== 'fixed') {
      CalculateRelativeTaskDates(this.draft, this.project?.internal_start_date, this.project?.internal_end_date);
    }
  }

  get error(): string {
    if (this.mode === 'relative' && ((!this.draft.start_on_project_start && !this.IsOffset(this.draft.start_date_relative))
      || (!this.draft.due_on_project_end && !this.IsOffset(this.draft.due_date_relative)))) {
      return 'Enter how far from the project the task starts and is due (0 or more).';
    }
    return TaskDateError(this.draft, this.project?.internal_start_date, this.project?.internal_end_date);
  }

  private IsOffset(value: any): boolean {
    return value !== null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;
  }

  StartLabel(): string {
    return TaskStartDateLabel(this.draft);
  }

  DueLabel(): string {
    return TaskDueDateLabel(this.draft);
  }

  Save() {
    if (this.error) return;
    this.modalController.dismiss({ ...this.draft });
  }

  Cancel() {
    this.modalController.dismiss();
  }
}
